import React, { Component } from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';

import { ExperimentsConsumer } from '@spotahome/bff/client';
import { ToastContainer } from '@spotahome/ui-library';

import getSearchQuery from '@common/graphql/queries/search';

import SessionExpiredModal from '@common/components/SessionExpiredModal';
import FavoriteClaim from '@common/components/FavoriteClaim';
import Cookies from '@common/components/Cookies';
import HeaderContainer from '@common/components/HeaderContainer';
import ZendeskChatButton from '@common/components/ZendeskChatButton';
import InformationToastWrapper from '@common/components/InformationToast/index';
import { hasValidCityInfo } from '@common/components/InformationToast/informationToastUtils';

import CurrencyFormatter from '@utils/CurrencyFormatter';

import SahContainer from '../../LEGACY/javascripts/ui/sah-container';
import Search from '../../LEGACY/javascripts/search/components/search';

import { MQ_TABLET_MIN_WIDTH } from '../../common/constants';

import poiShape from './shapes/poi';
import cityShape from './shapes/city';

import { getInitialFilters, adaptFilters } from './utils/filters';
import { buildHeadersData } from './builders/headers';
import { buildAppData } from './builders/app';

import { Provider } from './IsUserLoggedContext';

const TOAST_ENTER_TIMEOUT = 3000;
const SCROLL_SETTINGS = {
  elementId: 'search-scroll'
};

const getMetadataUrl = (url, city) => {
  const startIndex = url.indexOf(`/${city}`);
  const endIndex = url.length;

  return url.substring(startIndex, endIndex);
};

const getMetadataLocale = locale => (locale === 'en' ? '' : locale);
const MARKERS_PER_PAGE = 60;
const SHOULD_SHIFT_MARKERS = true;

class SearchPage extends Component {
  static NAME = 'SearchPage';

  static contextTypes = {
    data: PropTypes.shape({
      SearchPage: PropTypes.shape({
        poi: PropTypes.shape(poiShape),
        city: PropTypes.shape(cityShape).isRequired,
        maxPriceInCity: PropTypes.number.isRequired,
        seoLinks: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired
          })
        ),
        initialFiltersFromQuery: PropTypes.object.isRequired
      }).isRequired
    }).isRequired
  };

  static async requestAllData(req, _, { clientConfig, graphqlClient }) {
    const { params, originalUrl, locale } = req;
    const searchAlgorithmNamespace = req.getNamespace(
      'SearchAlgorithmNamespace'
    );

    const initialFiltersFromQuery = getInitialFilters(originalUrl);

    const { data } = await getSearchQuery(graphqlClient)(
      params.city,
      adaptFilters(
        initialFiltersFromQuery,
        searchAlgorithmNamespace.get('algorithm_name')
      ),
      getMetadataLocale(locale),
      initialFiltersFromQuery['move-in'],
      initialFiltersFromQuery['move-out'],
      getMetadataUrl(originalUrl, params.city),
      MARKERS_PER_PAGE
    );

    const { metadata, ...searchData } = data;
    const { paginator } = searchData.search;

    return {
      headers: buildHeadersData(
        params.city,
        clientConfig,
        metadata,
        initialFiltersFromQuery,
        paginator,
        req
      ),
      data: buildAppData(
        searchData,
        initialFiltersFromQuery,
        SHOULD_SHIFT_MARKERS
      )
    };
  }

  state = {
    ...this.context.data[SearchPage.NAME]
  };

  componentDidMount = () => {
    return (
      hasValidCityInfo(this.state.city.id) &&
      setTimeout(
        () => InformationToastWrapper('freeComission', this.state.city.id),
        TOAST_ENTER_TIMEOUT
      )
    );
  };

  render() {
    const {
      city,
      allCities,
      poi,
      maxPriceInCity,
      initialFiltersFromQuery,
      initialMarkerList = [],
      initialHomecards = {},
      initialUnitPrices,
      isUserLogged,
      numberOfMarkers
    } = this.state;

    const countryCode = city.country.countryIsoCode;
    const cities = city.allCities;
    const otherCitiesInCountry = city.otherCitiesInCountry;
    const currency = new CurrencyFormatter(city.currencyIsoCode);

    return (
      <SahContainer>
        <HeaderContainer withAppBanner />
        <ToastContainer />
        <Provider
          value={{
            isUserLogged,
            allCities
          }}
        >
          <ExperimentsConsumer namespace="SearchAlgorithmNamespace">
            {searchAlgorithmNamespace => (
              <Search
                sessionPoi={poi}
                area={city}
                countryCode={countryCode}
                currency={currency}
                maxPriceInCity={maxPriceInCity}
                initialFiltersFromQuery={initialFiltersFromQuery}
                allCities={cities}
                otherCitiesInCountry={otherCitiesInCountry}
                initialMarkerList={initialMarkerList}
                initialHomecards={initialHomecards}
                initialUnitPrices={initialUnitPrices}
                numberOfMarkers={numberOfMarkers}
                sortingAlgorithm={searchAlgorithmNamespace.get(
                  'algorithm_name'
                )}
                itemsPerPage={MARKERS_PER_PAGE}
              />
            )}
          </ExperimentsConsumer>
        </Provider>
        <ZendeskChatButton />
        <SessionExpiredModal />
        <FavoriteClaim />
        <MediaQuery minWidth={MQ_TABLET_MIN_WIDTH}>
          {matches => {
            const cookiesProps = matches
              ? { scrollSettings: SCROLL_SETTINGS }
              : {};

            return <Cookies {...cookiesProps} />;
          }}
        </MediaQuery>
      </SahContainer>
    );
  }
}

export default SearchPage;
